import { Action } from "redux";
import { ThunkAction } from "redux-thunk";
import { RootState } from "..";
import { IStoreItem } from "../../interfaces/IStoredItem";
import { typedAction } from "../helpers";
import { IRecentItems } from "../models/reduxModels";
import { getRecentFiles } from "../../services/GraphService";
import {
  RESET_USER_RECENT_ITEMS,
  SET_USER_RECENT_ITEMS,
  SET_RECENT_IS_LOADING
} from "./actionTypes";
import { FileTypeEnum } from "../../components/enums/fileTypeEnum";
import { ArrayIdCombiner } from "../../components/helpers/arrayCombiner";
import { RecentFilesMapper } from "../../components/mappers/recentFilesMapper";
import { StoreItemHelper } from "../../components/helpers/storeItemHelper";
import dayjs from 'dayjs';
import { logger } from "../../../../utilities/logger";
import LZString = require("lz-string");
import { apiGetCompressedRecentData, apiGetOldRecentItems, apiSetRecentItems } from "../../services/ApiService";

const initialState: IRecentItems = {
  isRecentItemsLoading: false,
  recentItems: null
};

const setIsRecentItemsLoading = (isRecentItemsLoading: boolean) => {
  return typedAction(SET_RECENT_IS_LOADING, isRecentItemsLoading);
};

export const setRecentItems = (recentItems: IStoreItem[]) => {
  return typedAction(SET_USER_RECENT_ITEMS, recentItems);
};

export const resetRecentItems = () => {
  return typedAction(RESET_USER_RECENT_ITEMS);
};

export const getRecentItems = (accountId: string, languageStrings: any): ThunkAction<Promise<number>, RootState, unknown, Action<string>> => async (dispatch, getState) => {
  if (getState().recent?.isRecentItemsLoading) {
    return -1;
  }

  dispatch(setIsRecentItemsLoading(true));

  const getLocalRecentItems = async () => {
    let localRecentItems: IStoreItem[];
    try {
      let compressedRecentData: string = await apiGetCompressedRecentData(accountId);
      if (compressedRecentData) {
        let fallback: boolean = false;
        try {
          const decompressedData = LZString.decompressFromBase64(compressedRecentData);
          if (decompressedData) {
            localRecentItems = JSON.parse(decompressedData);
          } else {
            fallback = true;
          }
        } catch (error) {
          // Fallback for old structured data
          fallback = true;
        }
        if (fallback) {
          try {
            localRecentItems = await apiGetOldRecentItems(accountId)
          } catch (e) {
            localRecentItems = null;
          }
        }
      }
  
      if (localRecentItems && Array.isArray(localRecentItems)) {
        localRecentItems = localRecentItems
          .map<IStoreItem>(recentItem => {
            return {
              ...recentItem,
              datePinned: recentItem.datePinned && dayjs(recentItem.datePinned).toDate()
            }
          })
          .sort((a, b) => a?.datePinned && b?.datePinned && +a.datePinned < +b.datePinned ? 1 : -1)
          .slice(0, 25);
      }
    } catch (error) {
      logger(error, "recentItems.ts (getLocalRecentItems)");
    }
    return localRecentItems;
  }

  let recentItems: IStoreItem[];
  let graphRecentFiles: any;

  [recentItems, graphRecentFiles] = await Promise.all([getLocalRecentItems(), getRecentFiles(true)]);

  let graphRemovedDuplicates: IStoreItem[];
  try {
    if (graphRecentFiles) {
      const graphRecentStoreItems = new RecentFilesMapper(languageStrings, true).map(graphRecentFiles);
      graphRemovedDuplicates = recentItems ? ArrayIdCombiner.removeDuplicates(graphRecentStoreItems, recentItems.map(x => x.itemId)) : graphRecentStoreItems;
    }
  } catch (error) {
    logger(error, "recentItems.ts (Remove duplicates)");
  }

  const combined: IStoreItem[] = [...(recentItems ?? []), ...(graphRemovedDuplicates ?? [])].sort((a, b) => {
    // return a?.lastModifiedDateTime?.length && b?.lastModifiedDateTime?.length && dayjs(a.lastModifiedDateTime).unix() < dayjs(b.lastModifiedDateTime).unix() ? 1 : -1;
    return a?.datePinned && b?.datePinned && +a.datePinned < +b.datePinned ? 1 : -1;
  });

  dispatch(setRecentItems(combined));
  return combined.length;
};

export const addRecentItem = (accountId: string, newRecentItems: IStoreItem[]): ThunkAction<void, RootState, unknown, Action<string>> => (dispatch, getState) => {
  if (newRecentItems && newRecentItems?.length > 0) {
    let updatedRecentItems: IStoreItem[];
    try {
      const existingRecentItems: IStoreItem[] = getState().recent.recentItems?.filter(existingRecentItem => !StoreItemHelper.itemExists(existingRecentItem, newRecentItems));

      updatedRecentItems = existingRecentItems
        ? newRecentItems.concat(existingRecentItems)
        : newRecentItems;

      apiSetRecentItems(accountId, updatedRecentItems);
    } catch (e) {
      logger(e, "recentItems.ts (addRecentItems)");
    }
    dispatch(setRecentItems(updatedRecentItems));
  }
};

export const removeRecentItem = (accountId: string, item: { itemId: string; driveId: string }, fileType: FileTypeEnum): ThunkAction<void, RootState, unknown, Action<string>> => async (dispatch, getState) => {
  const existingItems: IStoreItem[] = getState().recent.recentItems ?? [];
  let updatedItems: IStoreItem[];
  try {
    if (fileType === FileTypeEnum.folder) {
      updatedItems = !item.itemId
        ? existingItems.filter(x => x.driveId !== item.driveId || x.itemId !== item.itemId)
        : existingItems.filter(x => x.itemId !== item.itemId);
    }
    else {
      updatedItems = existingItems.filter(x => x.itemId !== item.itemId);
    }

    apiSetRecentItems(accountId, updatedItems);
  } catch (e) {
    logger(e, "recentItems.ts (removeRecentItems)");
  }

  dispatch(setRecentItems(updatedItems));
};


type HomeAction = ReturnType<
  | typeof setRecentItems
  | typeof resetRecentItems
  | typeof setIsRecentItemsLoading
>;

export function recentReducer(state = initialState, action: HomeAction): IRecentItems {
  switch (action.type) {
    case SET_RECENT_IS_LOADING:
      return { ...state, isRecentItemsLoading: action.payload };
    case SET_USER_RECENT_ITEMS:
      return { isRecentItemsLoading: false, recentItems: action.payload };
    case RESET_USER_RECENT_ITEMS:
      return initialState;
    default:
      return state;
  }
}
